import {
  ActionItem,
  ActionItemText,
  Anchor,
  BookOpen01Icon,
  Button,
  ChevronDownIcon,
  Copy01Icon,
  Edit04Icon,
  InfoCircleIcon,
  Microphone01Icon,
  Share07Icon,
  Skeleton,
  SwitchHorizontal01Icon,
  VolumeMaxIcon,
  XCloseIcon
} from '@translated/babele-react'
import clsx from 'clsx'
import {Trans, useTranslation} from 'react-i18next'
import {
  DocumentsIllustration,
  InterpreterIllustration,
  TranslateIllustration
} from './Illustrations'
import {TLink} from '~/features/TLink'
import * as SwitchPrimitives from '@radix-ui/react-switch'

export const ActionButton = ({
  illustration,
  label,
  isActive
}: {
  isActive?: boolean
  illustration: React.ReactNode
  label: string
}) => {
  return (
    <ActionItem
      className="relative flex items-center gap-2 !rounded-sm !p-3 lg:!py-3 lg:!pe-5 lg:!ps-4"
      variant={{size: 'md'}}
      render={({children, className}) => (
        <button
          type="button"
          className={clsx(
            className,
            isActive && 'bg-primary-200 text-primary-1000',
            'hover:bg-primary-100 hover:text-primary-1000 focus-visible:bg-primary-200'
          )}
        >
          {children}
        </button>
      )}
    >
      {illustration}
      <ActionItemText className="font-medium max-lg:sr-only">{label}</ActionItemText>
    </ActionItem>
  )
}

export function TranslateSkeleton({showLanguagesSkeleton}: {showLanguagesSkeleton: boolean}) {
  const {t} = useTranslation('translate')

  return (
    <section className="absolute inset-0 flex size-full grow flex-col rounded-t-lg lg:rounded-lg">
      <div className="flex justify-between border-b-1 border-neutral-300 p-3 lg:border-transparent lg:p-4">
        {/* Navigation bar */}
        <div className="flex lg:gap-2">
          <ActionButton
            isActive
            illustration={<TranslateIllustration className="size-8 lg:size-10" />}
            label={t('translate_skeleton.navigation_menu.translate_text')}
          />
          <ActionButton
            illustration={<DocumentsIllustration className="size-8 lg:size-10" />}
            label={t('translate_skeleton.navigation_menu.translate_documents')}
          />
          <ActionButton
            illustration={<InterpreterIllustration className="size-8 lg:size-10" />}
            label={t('translate_skeleton.navigation_menu.interpreter')}
          />
        </div>

        {/* Incognito switch */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 pe-1 md:gap-2.5 md:pe-2 lg:gap-2 lg:pe-3">
            <label
              className="cursor-pointer text-end text-sm text-neutral-1000"
              htmlFor="incognitoSwitchCondensed"
            >
              <span className="inline transition-colors lg:hidden">
                {t('translate_skeleton.incognito_label_mobile')}
              </span>
              <span className="hidden font-medium transition-colors lg:inline">
                {t('translate_skeleton.incognito_label')}
              </span>
            </label>
            <SwitchPrimitives.Root
              data-theme="translated-light"
              className="group inline-flex h-8 w-12 shrink-0 cursor-pointer items-center rounded-xl border-2 border-neutral-300 bg-neutral-300 transition-colors checked:border-primary-800 checked:bg-primary-800 disabled:cursor-not-allowed disabled:opacity-5 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-500/5"
            >
              <SwitchPrimitives.Thumb className="pointer-events-none grid aspect-square size-6 translate-x-1 place-items-center rounded-xl bg-neutral-25 text-sm shadow-md transition-all data-[state=checked]:translate-x-4 data-[state=checked]:text-primary-1100 data-[state=unchecked]:text-neutral-800 md:text-md rtl:-translate-x-1 rtl:data-[state=checked]:-translate-x-4">
                <XCloseIcon />
              </SwitchPrimitives.Thumb>
            </SwitchPrimitives.Root>
          </div>
          <div className="h-6 border-1 border-neutral-300 transition-colors" />
          <Button
            variant={{mode: 'ghost', size: 'md'}}
            aria-label={t('translation_mode.button_aria_label')}
            className="!text-neutral-1000"
          >
            <InfoCircleIcon className="size-3 lg:size-4" />
          </Button>
        </div>
      </div>
      <div className="flex size-full w-full grow flex-col">
        {/* Mobile language switch */}
        <div className="relative flex w-full gap-2 p-3 !text-md lg:hidden">
          <Button
            type="button"
            variant={{mode: 'outline', size: 'md'}}
            aria-label={t('switch_languages_button')}
            className="w-1/2 border-neutral-300 px-3 py-6 !text-md !text-primary-1100 disabled:pointer-events-auto"
          >
            {showLanguagesSkeleton ? (
              <Skeleton isLoading className="h-2 w-1/3 rounded-sm bg-neutral-200" />
            ) : (
              t('translate_skeleton.detect_language')
            )}
          </Button>
          <Button
            type="button"
            variant={{mode: 'outline', size: 'sm'}}
            aria-label={t('switch_languages_button')}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 border-neutral-300 bg-neutral-25 px-1 py-3 text-neutral-600 transition-all disabled:pointer-events-auto"
          >
            <SwitchHorizontal01Icon />
          </Button>
          <Button
            type="button"
            variant={{mode: 'outline', size: 'md'}}
            aria-label={t('switch_languages_button')}
            className="w-1/2 border-neutral-300 px-3 py-6 !text-md !text-primary-1100 disabled:pointer-events-auto"
          >
            {showLanguagesSkeleton ? (
              <Skeleton isLoading className="h-2 w-1/3 rounded-sm bg-neutral-200" />
            ) : (
              t('languages.it', {ns: 'common'})
            )}
          </Button>
        </div>

        {/* Mobile styles switch and glossaries */}
        <div className="flex items-center justify-between p-3 pt-0 lg:hidden">
          <div className="flex gap-1 p-1">
            <Button
              disabled
              className="!hover:bg-primary-300 h-8 rounded-xs bg-primary-400 !px-3 !text-primary-1100 transition-colors"
            >
              {t('translate_skeleton.styles.faithful')}
            </Button>
            <Button
              disabled
              className="h-8 rounded-xs !bg-transparent !px-3 !text-neutral-1000 transition-colors"
            >
              {t('translate_skeleton.styles.fluid')}
            </Button>
            <Button
              disabled
              className="h-8 rounded-xs !bg-transparent !px-3 !text-neutral-1000 transition-colors"
            >
              {t('translate_skeleton.styles.creative')}
            </Button>
          </div>
          <Button
            aria-label={t('translate_skeleton.glossaries')}
            variant={{mode: 'ghost', size: 'sm'}}
          >
            <BookOpen01Icon />
          </Button>
        </div>

        {/* Main content */}
        <div className="relative flex size-full grow flex-col gap-3 px-3 pb-3 md:gap-2 md:px-4 md:pb-4 lg:flex-row">
          {/* Source area */}
          <div className="flex grow flex-col gap-2 rounded-md border-1 border-neutral-300 bg-neutral-100 p-2 md:p-4 lg:w-1/2">
            <div className="hidden items-center justify-between pe-5 lg:flex">
              <button
                type="button"
                className="flex h-12 items-center gap-2 px-[0.8rem] font-medium text-primary-1100"
              >
                {showLanguagesSkeleton ? (
                  <Skeleton isLoading className="h-2 w-[8rem] rounded-sm bg-neutral-200" />
                ) : (
                  t('translate_skeleton.detect_language')
                )}
                <ChevronDownIcon />
              </button>
              <Button
                disabled
                variant={{mode: 'ghost', size: 'sm'}}
                className="!px-[0.6rem] !py-5 !text-neutral-1100 disabled:opacity-full"
              >
                <Edit04Icon />
                {t('translate_skeleton.add_context')}
              </Button>
            </div>
            <div className="grow rounded-sm bg-neutral-200 px-[1.05rem] py-[0.8rem] text-lg text-neutral-800 md:px-[1.55rem] md:py-[1.05rem] lg:text-display-xs">
              {t('translate_skeleton.source_text_placeholder')}
            </div>
            <div className="flex items-center justify-between">
              <div className="flex gap-5 p-2.5 text-neutral-700">
                <Microphone01Icon className="!size-5" />
                <VolumeMaxIcon className="!size-5" />
              </div>
              <Button
                disabled
                variant={{mode: 'outline', size: 'sm'}}
                className="border-neutral-400 !px-3 !py-[1.1875rem] !text-neutral-1100 disabled:opacity-full lg:hidden"
              >
                <Edit04Icon />
                <span className="hidden md:block">{t('translate_skeleton.add_context')}</span>
              </Button>
            </div>
          </div>

          {/* Desktop language switch */}
          <Button
            type="button"
            variant={{mode: 'outline', size: 'sm'}}
            aria-label={t('switch_languages_button')}
            className="absolute left-1/2 top-4 hidden -translate-x-1/2 border-neutral-400 bg-neutral-25 px-[0.68rem] py-[1.18rem] font-medium text-neutral-600 lg:flex"
          >
            <SwitchHorizontal01Icon />
          </Button>

          {/* Target area */}
          <div className="flex h-auto grow flex-col gap-2 rounded-md border-1 border-neutral-300 bg-neutral-100 p-2 md:p-4 lg:w-1/2">
            <div className="hidden items-center justify-between lg:flex">
              <button
                type="button"
                className="flex h-12 items-center gap-2 px-[1.8rem] font-medium text-primary-1100"
              >
                {showLanguagesSkeleton ? (
                  <Skeleton isLoading className="h-2 w-[8rem] rounded-sm bg-neutral-200" />
                ) : (
                  t('languages.it', {ns: 'common'})
                )}
                <ChevronDownIcon />
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="flex h-fit items-center gap-1 rounded-sm border-1 border-neutral-300 px-3 py-[0.55rem] text-sm font-medium"
                >
                  {t('translate_skeleton.glossaries')}
                  <ChevronDownIcon />
                </button>
                <button
                  type="button"
                  className="flex h-fit items-center gap-1 rounded-sm border-1 border-neutral-300 px-3 py-[0.55rem] text-sm font-medium"
                >
                  {t('translate_skeleton.styles.faithful')}
                  <ChevronDownIcon />
                </button>
              </div>
            </div>
            <div className="grow rounded-sm bg-neutral-200 px-[1.05rem] py-[0.8rem] text-lg text-neutral-800 md:px-[1.55rem] md:py-[1.05rem] lg:text-display-xs">
              {t('translate_skeleton.target_text_placeholder')}
            </div>
            <div className="flex gap-5 p-2.5 text-neutral-700">
              <VolumeMaxIcon className="!size-5" />
              <Copy01Icon className="!size-5" />
              <Share07Icon className="!size-5" />
            </div>
          </div>
        </div>
      </div>

      <div className="hidden pb-4 pt-2 text-center text-sm lg:block">
        <Trans
          t={t}
          i18nKey="translate_skeleton.disclaimer"
          components={{
            '1': (
              <Anchor
                className="text-md"
                render={<TLink to="/terms-and-conditions" target="_blank" />}
              >
                Link
              </Anchor>
            ),
            '3': (
              <Anchor className="text-md" render={<TLink to="/privacy-policy" target="_blank" />}>
                Link
              </Anchor>
            )
          }}
        />
      </div>
    </section>
  )
}
